import React from 'react';
import {
    BadgeCheck,
    BarChart3,
    Check,
    FileCheck2,
    FileText,
    Folder,
    LockKeyhole,
    MapPin,
    Search,
    Send,
    ShieldCheck,
    Sparkles,
} from 'lucide-react';
import {
    OperationsIllustrationShellProps,
    OperationsWorkerIllustration,
    SelectionIllustrationProps,
    SmartMatchCandidateIllustration,
    TrustVaultFolderRow,
    WorkypayPaymentRow,
    WorkypayStatusCard,
} from './LandingPageProductsOperationsSelectionComponentsInterfaces';

const OPERATIONS_WORKERS: readonly OperationsWorkerIllustration[] = [
    {
        initials: 'EC',
        name: 'Emily Carter',
        role: 'PSW',
        status: 'Available',
        statusClassName: 'ops-psc-status-available',
    },
    {
        initials: 'JL',
        name: 'James Lee',
        role: 'RN',
        status: 'On Shift',
        statusClassName: 'ops-psc-status-on-shift',
    },
    {
        initials: 'MG',
        name: 'Maria Gomez',
        role: 'RPN',
        status: 'Available',
        statusClassName: 'ops-psc-status-available',
    },
    {
        initials: 'AT',
        name: 'Alex Thompson',
        role: 'PSW',
        status: 'On Shift',
        statusClassName: 'ops-psc-status-on-shift',
    },
];

const OperationsIllustrationShell = ({
    children,
    className = '',
}: OperationsIllustrationShellProps): React.ReactElement => {
    const shellClassName: string = className
        ? `ops-psc-shell ${className}`
        : 'ops-psc-shell';

    return (
        <div className={shellClassName}>
            <div className="ops-psc-shell-blur-left" />
            <div className="ops-psc-shell-blur-right" />
            <div className="ops-psc-shell-inner">{children}</div>
        </div>
    );
};

const InvoicingSelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell>
                <div className="ops-psc-invoicing-card">
                    <div className="ops-psc-invoicing-card-header">
                        <div>
                            <p className="ops-psc-label-muted">INVOICE</p>
                            <p className="ops-psc-invoicing-number">#INV-1024</p>
                        </div>
                        <span className="ops-psc-badge-paid">Paid</span>
                    </div>

                    <div className="ops-psc-invoicing-client">
                        <p className="ops-psc-label-muted">Client</p>
                        <p className="ops-psc-invoicing-client-name">Acme Healthcare</p>
                    </div>

                    <div className="ops-psc-invoicing-metrics">
                        <div>
                            <p className="ops-psc-label-muted">Hours</p>
                            <p className="ops-psc-metric-value">40</p>
                        </div>
                        <div>
                            <p className="ops-psc-label-muted">Rate</p>
                            <p className="ops-psc-metric-value">$120</p>
                        </div>
                        <div>
                            <p className="ops-psc-label-muted">Total</p>
                            <p className="ops-psc-metric-value">$4,800</p>
                        </div>
                    </div>
                </div>

                <div className="ops-psc-invoicing-auto-billed">
                    <Sparkles className="ops-psc-icon-cyan" size={16} />
                    <span className="ops-psc-chip-text">Auto-billed</span>
                </div>

                <div className="ops-psc-invoicing-sent">
                    <span className="ops-psc-check-circle">
                        <Check className="ops-psc-check-icon" size={12} />
                    </span>
                    <span className="ops-psc-chip-text">Sent to client</span>
                </div>

                <div className="ops-psc-invoicing-revenue">
                    <p className="ops-psc-label-muted">Revenue</p>
                    <p className="ops-psc-revenue-value">$98,250</p>
                    <p className="ops-psc-revenue-change">+24%</p>
                    <svg viewBox="0 0 120 36" className="ops-psc-revenue-chart">
                        <path
                            d="M2 29 L20 18 L38 23 L56 12 L74 16 L92 8 L118 3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="ops-psc-revenue-chart-line"
                        />
                    </svg>
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

const WorkypaySelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const payments: readonly WorkypayPaymentRow[] = [
        { name: 'Sarah Johnson', amount: '$1,250.00' },
        { name: 'Mike Chen', amount: '$1,150.00' },
        { name: 'Priya Patel', amount: '$1,300.00' },
        { name: 'David Wilson', amount: '$1,100.00' },
    ];

    const statusCards: readonly WorkypayStatusCard[] = [
        {
            icon: <BadgeCheck size={16} />,
            title: 'Direct Deposit',
            subtitle: 'Completed',
        },
        {
            icon: <FileCheck2 size={16} />,
            title: 'Tax Remittance',
            subtitle: 'Filed',
        },
        {
            icon: <Send size={16} />,
            title: 'Payslips Sent',
            subtitle: '32 employees',
        },
    ];

    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell>
                <div className="ops-psc-workypay-card">
                    <div className="ops-psc-workypay-card-header">
                        <div>
                            <p className="ops-psc-label-muted">Payroll Run</p>
                            <p className="ops-psc-workypay-total">$45,231.00</p>
                        </div>
                        <span className="ops-psc-badge-completed">Completed</span>
                    </div>

                    <div className="ops-psc-workypay-rows">
                        {payments.map((payment: WorkypayPaymentRow, index: number) => (
                            <div key={payment.name} className="ops-psc-workypay-row">
                                <div className="ops-psc-workypay-row-left">
                                    <span className="ops-psc-workypay-index">{index + 1}</span>
                                    <span className="ops-psc-workypay-name">{payment.name}</span>
                                </div>
                                <span className="ops-psc-workypay-amount">{payment.amount}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ops-psc-workypay-status-stack">
                    {statusCards.map((item: WorkypayStatusCard) => (
                        <div key={item.title} className="ops-psc-workypay-status-card">
                            <span className="ops-psc-icon-emerald">{item.icon}</span>
                            <div>
                                <p className="ops-psc-status-title">{item.title}</p>
                                <p className="ops-psc-status-subtitle">{item.subtitle}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

const SmartMatchSelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const candidates: readonly SmartMatchCandidateIllustration[] = [
        {
            initials: 'JM',
            name: 'Jessica M.',
            role: 'PSW',
            distance: '3.2 km away',
            active: false,
        },
        {
            initials: 'DK',
            name: 'Daniel K.',
            role: 'RN',
            distance: '1.8 km away',
            active: true,
        },
        {
            initials: 'AP',
            name: 'Aisha P.',
            role: 'RPN',
            distance: '2.5 km away',
            active: false,
        },
    ];

    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell className="ops-psc-shell-smart-match">
                <div className="ops-psc-smart-match-map">
                    <svg viewBox="0 0 220 220" className="ops-psc-smart-match-map-svg">
                        <path
                            d="M10 30 L70 70 L120 20 L200 80 M0 130 L75 90 L125 145 L210 110 M45 220 L90 145 L160 210"
                            fill="none"
                            stroke="#94a3b8"
                            strokeWidth="2"
                        />
                        <path
                            d="M20 10 L45 220 M120 0 L90 220 M200 10 L160 220"
                            fill="none"
                            stroke="#cbd5e1"
                            strokeWidth="1.5"
                        />
                    </svg>
                </div>

                <div className="ops-psc-smart-match-pin">
                    <MapPin className="ops-psc-smart-match-pin-icon" size={48} />
                    <div className="ops-psc-smart-match-pin-tail" />
                </div>

                <div className="ops-psc-smart-match-list">
                    {candidates.map((candidate: SmartMatchCandidateIllustration) => (
                        <div
                            key={candidate.name}
                            className={
                                candidate.active
                                    ? 'ops-psc-smart-match-candidate ops-psc-smart-match-candidate-active'
                                    : 'ops-psc-smart-match-candidate'
                            }
                        >
                            <span className="ops-psc-smart-match-avatar">{candidate.initials}</span>
                            <div className="ops-psc-smart-match-details">
                                <div className="ops-psc-smart-match-name-row">
                                    <p className="ops-psc-smart-match-name">{candidate.name}</p>
                                    <span className="ops-psc-smart-match-score">
                                        {candidate.active ? '94% match' : ''}
                                    </span>
                                </div>
                                <div className="ops-psc-smart-match-meta-row">
                                    <p className="ops-psc-smart-match-meta">{candidate.role}</p>
                                    <p className="ops-psc-smart-match-meta">{candidate.distance}</p>
                                </div>
                                <div className="ops-psc-smart-match-stars">
                                    {Array.from({ length: 5 }).map((_, starIndex: number) => (
                                        <span key={starIndex} className="ops-psc-star">
                                            ★
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

const TimeClockSelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const bars: readonly number[] = [28, 40, 33, 54, 67, 92];
    const weekDays: readonly string[] = ['M', 'T', 'W', 'T', 'F'];
    const weekActive: readonly boolean[] = [true, false, true, true, false];

    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell>
                <div className="ops-psc-timeclock-today">
                    <p className="ops-psc-timeclock-date">Today, May 24</p>
                    <p className="ops-psc-timeclock-timer">08:24:19</p>
                    <p className="ops-psc-label-muted">Hours Worked</p>

                    <div className="ops-psc-timeclock-clocked-in">
                        <span className="ops-psc-check-circle">
                            <Check className="ops-psc-check-icon" size={12} />
                        </span>
                        Clocked In
                    </div>
                    <p className="ops-psc-timeclock-clock-time">9:00 AM</p>

                    <div className="ops-psc-timeclock-location-block">
                        <p className="ops-psc-label-muted-sm">Location</p>
                        <div className="ops-psc-timeclock-location">
                            <MapPin className="ops-psc-icon-emerald-sm" size={12} />
                            Toronto, ON
                        </div>
                    </div>
                </div>

                <div className="ops-psc-timeclock-week">
                    <div className="ops-psc-timeclock-week-days">
                        {weekDays.map((day: string, index: number) => (
                            <span key={`${day}-${index}`}>{day}</span>
                        ))}
                    </div>
                    <div className="ops-psc-timeclock-week-dots">
                        {weekActive.map((active: boolean, index: number) => (
                            <span
                                key={index}
                                className={
                                    active
                                        ? 'ops-psc-timeclock-dot ops-psc-timeclock-dot-active'
                                        : 'ops-psc-timeclock-dot'
                                }
                            />
                        ))}
                    </div>
                </div>

                <div className="ops-psc-timeclock-hours">
                    <div className="ops-psc-timeclock-hours-header">
                        <div>
                            <p className="ops-psc-label-muted-sm">Total Hours</p>
                            <p className="ops-psc-timeclock-hours-value">40.25</p>
                        </div>
                        <BarChart3 className="ops-psc-icon-emerald" size={16} />
                    </div>
                    <div className="ops-psc-timeclock-bars">
                        {bars.map((height: number, index: number) => (
                            <div
                                key={index}
                                className="ops-psc-timeclock-bar"
                                style={{ height: `${height}%` }}
                            />
                        ))}
                    </div>
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

const WorkforceSelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell>
                <div className="ops-psc-workforce-panel">
                    <div className="ops-psc-workforce-header">
                        <div>
                            <p className="ops-psc-label-muted">Talent pool</p>
                            <p className="ops-psc-workforce-title">Workers</p>
                        </div>
                        <div className="ops-psc-workforce-search">
                            <Search className="ops-psc-workforce-search-icon" size={12} />
                            <span className="ops-psc-workforce-search-text">Search workers...</span>
                        </div>
                    </div>

                    <div>
                        {OPERATIONS_WORKERS.map((worker: OperationsWorkerIllustration) => (
                            <div key={worker.name} className="ops-psc-workforce-row">
                                <span className="ops-psc-workforce-avatar">{worker.initials}</span>
                                <p className="ops-psc-workforce-name">{worker.name}</p>
                                <p className="ops-psc-workforce-role">{worker.role}</p>
                                <span className={`ops-psc-workforce-status ${worker.statusClassName}`}>
                                    {worker.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

const TrustVaultSelectionIllustration = ({
    className = '',
}: SelectionIllustrationProps): React.ReactElement => {
    const folders: readonly TrustVaultFolderRow[] = [
        { name: 'Certifications', count: '12 files' },
        { name: 'Background Checks', count: '8 files' },
        { name: 'Contracts', count: '15 files' },
        { name: 'Training Records', count: '9 files' },
    ];

    const rootClassName: string = className ? `ops-psc-root ${className}` : 'ops-psc-root';

    return (
        <div className={rootClassName}>
            <OperationsIllustrationShell>
                <div className="ops-psc-trustvault-docs">
                    <div className="ops-psc-trustvault-docs-header">
                        <div>
                            <p className="ops-psc-label-muted-sm">Secure storage</p>
                            <p className="ops-psc-trustvault-docs-title">Documents</p>
                        </div>
                        <FileText className="ops-psc-icon-muted" size={16} />
                    </div>

                    <div className="ops-psc-trustvault-folder-list">
                        {folders.map((folder: TrustVaultFolderRow) => (
                            <div key={folder.name} className="ops-psc-trustvault-folder-row">
                                <div className="ops-psc-trustvault-folder-left">
                                    <Folder className="ops-psc-trustvault-folder-icon" size={16} />
                                    <span className="ops-psc-trustvault-folder-name">{folder.name}</span>
                                </div>
                                <span className="ops-psc-trustvault-folder-count">{folder.count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ops-psc-trustvault-secure">
                    <span className="ops-psc-trustvault-lock-wrap">
                        <LockKeyhole className="ops-psc-icon-emerald" size={24} />
                    </span>
                    <p className="ops-psc-trustvault-secure-text">Data encrypted and secure</p>
                    <ShieldCheck className="ops-psc-trustvault-shield" size={20} />
                </div>
            </OperationsIllustrationShell>
        </div>
    );
};

export {
    InvoicingSelectionIllustration,
    WorkypaySelectionIllustration,
    SmartMatchSelectionIllustration,
    TimeClockSelectionIllustration,
    WorkforceSelectionIllustration,
    TrustVaultSelectionIllustration,
};
